import { useEffect, useState, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShieldCheck, AlertOctagon, Siren, Loader2, Inbox } from "lucide-react";

import AppTopbar from "@/components/AppTopbar";
import BottomNav from "@/components/BottomNav";
import AlertCard from "@/components/AlertCard";
import { listAlerts, getStats } from "@/lib/alerts";
import { playRedAlertBeep } from "@/lib/sound";

const TABS = [
  { id: "active", label: "Actives" },
  { id: "closed", label: "Historique" },
];

export default function Dashboard() {
  const [tab, setTab] = useState("active");
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState({
    active: { green: 0, orange: 0, red: 0, total: 0 },
    closed_today: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const knownRedIdsRef = useRef(null); // null = first load (no toast); Set otherwise
  const titleRef = useRef(typeof document !== "undefined" ? document.title : "");

  const load = useCallback(async () => {
    try {
      setError(null);
      const [list, s] = await Promise.all([
        listAlerts({ status: tab, level: "all", limit: 100 }),
        getStats(),
      ]);
      setAlerts(list.items);
      setStats(s);

      // Detect new red alerts on the active tab → toast + beep + title flash
      if (tab === "active") {
        const reds = list.items.filter((a) => a.level === "red");
        const currentIds = new Set(reds.map((r) => r.id));
        if (knownRedIdsRef.current === null) {
          knownRedIdsRef.current = currentIds;
        } else {
          const newOnes = reds.filter((r) => !knownRedIdsRef.current.has(r.id));
          if (newOnes.length > 0) {
            for (const r of newOnes) {
              toast.error(
                `🔴 Nouvelle urgence — ${r.agent_snapshot?.full_name || "Agent"}`,
                {
                  description: r.address || "Adresse non précisée",
                  duration: 8000,
                }
              );
            }
            try {
              playRedAlertBeep();
            } catch (_e) {}
            // Title flash until focus
            try {
              const original = titleRef.current || document.title;
              document.title = "🔴 URGENCE — SignalAgent";
              const restore = () => {
                document.title = original;
                window.removeEventListener("focus", restore);
              };
              if (document.hasFocus()) {
                setTimeout(restore, 5000);
              } else {
                window.addEventListener("focus", restore);
                setTimeout(restore, 30000);
              }
            } catch (_e) {}
          }
          knownRedIdsRef.current = currentIds;
        }
      }
    } catch (e) {
      setError(e?.message || "Erreur de chargement.");
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  // Poll every 15s on active tab
  useEffect(() => {
    if (tab !== "active") return undefined;
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [tab, load]);

  const red = alerts.filter((a) => a.level === "red");
  const orange = alerts.filter((a) => a.level === "orange");
  const green = alerts.filter((a) => a.level === "green");

  return (
    <div className="min-h-screen bg-[var(--sa-subtle)] pb-24" data-testid="dashboard-screen">
      <AppTopbar subtitle="Tableau de bord équipe" />

      <main className="mx-auto max-w-xl px-4 pt-5">
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="grid grid-cols-3 gap-2.5"
        >
          <Stat label="Vert" value={stats.active.green} color="#22C55E" testId="stat-green" />
          <Stat label="Orange" value={stats.active.orange} color="#F59E0B" testId="stat-orange" />
          <Stat label="Rouge" value={stats.active.red} color="#EF4444" testId="stat-red" />
        </motion.section>

        <div className="mt-5 inline-flex rounded-full border border-slate-200 bg-white p-1" data-testid="dashboard-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`rounded-full px-4 py-1.5 text-[13px] font-semibold transition ${
                tab === t.id
                  ? "bg-[var(--sa-blue)] text-white shadow-sm"
                  : "text-slate-500 hover:text-slate-800"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="mt-16 flex items-center justify-center text-slate-400">
            <Loader2 size={22} className="animate-spin" />
          </div>
        ) : error ? (
          <div className="mt-6 rounded-[14px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : alerts.length === 0 ? (
          <EmptyState tab={tab} />
        ) : (
          <div className="mt-5 flex flex-col gap-5">
            {tab === "active" ? (
              <>
                <Section
                  icon={Siren}
                  title="Urgences"
                  count={red.length}
                  accent="#EF4444"
                  testId="section-red"
                  sticky={red.length > 0}
                >
                  {red.length === 0 ? (
                    <EmptySectionText>Aucune urgence en cours.</EmptySectionText>
                  ) : (
                    red.map((a) => <AlertCard key={a.id} alert={a} pulse />)
                  )}
                </Section>

                <Section
                  icon={AlertOctagon}
                  title="À surveiller"
                  count={orange.length}
                  accent="#F59E0B"
                  testId="section-orange"
                >
                  {orange.length === 0 ? (
                    <EmptySectionText>Aucune alerte orange.</EmptySectionText>
                  ) : (
                    orange.map((a) => <AlertCard key={a.id} alert={a} />)
                  )}
                </Section>

                <Section
                  icon={ShieldCheck}
                  title="RDV en cours"
                  count={green.length}
                  accent="#22C55E"
                  testId="section-green"
                >
                  {green.length === 0 ? (
                    <EmptySectionText>Aucun RDV en cours.</EmptySectionText>
                  ) : (
                    green.map((a) => <AlertCard key={a.id} alert={a} />)
                  )}
                </Section>
              </>
            ) : (
              <div className="flex flex-col gap-3">
                {alerts.map((a) => (
                  <AlertCard key={a.id} alert={a} showClosed />
                ))}
              </div>
            )}
          </div>
        )}

        <p className="mt-10 pb-2 text-center text-[11px] font-medium text-slate-400">
          Propulsé par C l'Agence
        </p>
      </main>

      <BottomNav />
    </div>
  );
}

function Stat({ label, value, color, testId }) {
  return (
    <div
      className="rounded-[14px] border border-slate-200 bg-white px-3 py-3"
      data-testid={testId}
    >
      <div className="flex items-center gap-1.5">
        <span className="h-2 w-2 rounded-full" style={{ background: color }} />
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          {label}
        </span>
      </div>
      <div className="mt-1 text-[22px] font-bold text-slate-900">{value}</div>
    </div>
  );
}

function Section({ icon: Icon, title, count, accent, children, testId, sticky = false }) {
  return (
    <section
      data-testid={testId}
      className={
        sticky
          ? "sticky top-[60px] z-10 -mx-4 rounded-[18px] bg-[var(--sa-subtle)]/95 px-4 py-2 backdrop-blur-md"
          : ""
      }
    >
      <div className="mb-2 flex items-center gap-2">
        <span
          className="flex h-6 w-6 items-center justify-center rounded-full"
          style={{ background: `${accent}22`, color: accent }}
        >
          <Icon size={14} strokeWidth={2.4} />
        </span>
        <h2 className="text-[13px] font-semibold text-slate-700">
          {title}{" "}
          <span className="text-slate-400">({count})</span>
        </h2>
      </div>
      <div className="flex flex-col gap-2.5">{children}</div>
    </section>
  );
}

function EmptySectionText({ children }) {
  return (
    <div className="rounded-[12px] border border-dashed border-slate-200 px-4 py-3 text-[12.5px] text-slate-400">
      {children}
    </div>
  );
}

function EmptyState({ tab }) {
  return (
    <div
      className="mt-10 flex flex-col items-center justify-center gap-3 rounded-[16px] border border-dashed border-slate-200 bg-white px-6 py-10 text-center"
      data-testid="dashboard-empty"
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-50 text-slate-400">
        <Inbox size={22} />
      </div>
      <div>
        <h3 className="text-[15px] font-semibold text-slate-900">
          {tab === "active" ? "Aucune alerte active" : "Aucune alerte clôturée"}
        </h3>
        <p className="mt-1 text-[13px] text-slate-500">
          Les feux déclenchés par l'équipe apparaîtront ici en temps réel.
        </p>
      </div>
    </div>
  );
}
